/* eslint-disable camelcase */
/* eslint-disable
  no-param-reassign,
  no-underscore-dangle,
  no-console,
*/
import { v4 as uuidv4 } from 'uuid';
import { make } from 'vuex-pathify';
import { format, parse } from 'date-fns';
import vuexTimeBomb from '@mgo/vuex-time-bomb';
import { post, put, get } from '../../http';
import { messaging } from '../../messaging';
import dropdownData from './mgo-states.json';

/**
 * transform MM/dd/yyyy into yyyy-MM-dd
 * @param {String} date - String of format YYYY-MM-DD or Empty
 * @return {String}
 */
const dateToMGODate = (date) => (date ? format(parse(date, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd') : '');

/**
 * prune fields set to empty string from object
 * @param {Object} obj
 * @return {Object}
 */
const pruneObject = (obj) => Object.keys(obj).reduce((agg, field) => {
  if (obj[field] !== '') {
    Object.assign(agg, {
      [field]: obj[field],
    });
  }
  return agg;
}, {});

/**
   * Generate a COA update payload
   * performs various transforms
   * @param {Object} context - Vuex Context
   * @param {Object} context.state
   */
export const getUpdatePayload = (state) => {
  const payload = {
    origin: 'MMICOA',
    uuid: state.uuid,
    informedDelivery: state.informedDelivery,
    moverType: state.moverType.toUpperCase(),
    forwardType: state.forwardType.toUpperCase(),
    startDate: dateToMGODate(state.startForwardingDate) || '',
    stopDate: dateToMGODate(state.endForwardingDate) || '',
    billingAddressType: state.verificationType || '',
    businessName: '',
    email: state.mover.email,
  };

  if (state.moverType === 'business') {
    payload.bizPromptEntireMoving = state.wholeCompany === 'yes' || false;
    payload.businessName = state.mover.firstName;
  }

  if (state.moverType === 'family') {
    payload.famPrompt1ShareLastName = state.sameLastName === 'yes' || false;
    payload.famPrompt2SameLastNameRemaining = state.sameLastNameRemaining === 'yes' || false;
  }

  return pruneObject(payload);
};

export const FAMILY_IS_INDIVIDUAL = 'FAMILY-TO-INDIVIDUAL';
export const FAMILY_CONTAINS_INDIVIDUAL = 'FAMILY-CONTAINS-INDIVIDUAL';
export const BUSINESS_IS_INDIVIDUAL = 'BUSINESS-TO-INDIVIDUAL';

/**
 * Validation Controller For Family Settings
 * @param {String} sameLastName - Do all individuals moving have the same last name
 * @param {String} sameLastNameStay - Are any individuals with the same last name remaining
 * @return {Boolean}
 */
export const isFamilySettingsValid = (sameLastName, sameLastNameStay) => {
  if (sameLastNameStay === 'yes') { return false; }
  if (sameLastNameStay === 'yes' && sameLastName === 'yes') { return false; }

  return true;
};

/**
 * Returns the scenario most appropriate based on the filers selections
 * @param {String} sameLastName - Do all individuals moving have the same
 * @param {String} sameLastNameStay - Do all individuals moving
 * @string {String}
 */
export const getFamilyValidationError = (sameLastName, sameLastNameStay) => {
  if (sameLastName === 'no' && sameLastNameStay === 'no') { return FAMILY_CONTAINS_INDIVIDUAL; }
  if (sameLastNameStay === 'yes') return FAMILY_IS_INDIVIDUAL;
  if (sameLastName === 'no' && sameLastNameStay === 'yes') { return FAMILY_IS_INDIVIDUAL; }

  return '';
};

/**
 * Determine if Address Is Valid
 * @param {String} statusCode - Ams Statis Code
 * @return {Boolean}
 */
export const isAmsMatch = (statusCode) => ['EXACT_MATCH', 'DEFAULT'].indexOf(statusCode) !== -1;

const addressMap = {
  oldAddress: 'OLD',
  newAddress: 'NEW',
  billingAddress: 'BILLING',
  preparerAddress: 'PREPARER',
};

const personMap = {
  mover: 'MOVER',
  preparer: 'PREPARER',
};

const defaultState = {
  // time bomb cache plugin
  timeBomb: null,

  // coa properties
  visitId: '',
  uuid: '',
  moverType: '',
  forwardType: '',
  startForwardingDate: '',
  endForwardingDate: '',
  informedDelivery: false,
  verificationType: '',
  isPreparer: false,
  cfpsid: '',
  dcoa: false,

  // family properties
  sameLastName: '',
  sameLastNameStay: '',

  // business properties
  wholeCompany: '',

  // mover person properties
  mover: {
    uuid: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    email: '',
    emailConfirm: '',
    phone: '',
    phoneType: '',
    dob: '',
  },

  preparer: {
    uuid: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    email: '',
    emailConfirm: '',
    phone: '',
    phoneType: '',
  },

  oldAddress: {
    uuid: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    poBox: '',
    urbanizationName: '',
    military: false,
    amsStatusCode: '',
  },

  newAddressCanBeOverridden: false,
  newAddress: {
    uuid: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    poBox: '',
    privateMailbox: '',
    urbanizationName: '',
    military: false,
    recentConstruction: false,
    overrideAms: false,
    amsStatusCode: '',
  },

  billingAddress: {
    uuid: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    poBox: '',
    privateMailbox: '',
    urbanizationName: '',
    military: false,
    amsStatusCode: '',
  },

  preparerAddress: {
    uuid: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    poBox: '',
    urbanizationName: '',
    military: false,
    amsStatusCode: '',
  },

  // UI controls
  failedValidAddressAttempts: 0,

  // api properties
  token: '',
  errors: {},
  config: {},
  rapidKeyToken: '',

  // mmx properties
  writeToProfile: false,
};

const mutations = {
  ...vuexTimeBomb.mutations(defaultState),
  ...make.mutations(defaultState),

  RESET_STATE(s) {
    Object.assign(s, defaultState);
  },

  SET_NESTED_UUID(state, { property, uuid }) {
    Object.assign(state, {
      [property]: {
        ...state[property],
        uuid,
      },
    });
  },

  SET_ADDRESS_ERROR(state, { addressType, exception }) {
    Object.assign(state, {
      errors: {
        ...state.errors,
        [addressType]: exception,
      },
    });
  },

  SET_ADDRESS_AMS_STATUS(state, { addressType, amsStatusCode }) {
    let { failedValidAddressAttempts } = state;
    if (addressType === 'newAddress' && !isAmsMatch(amsStatusCode)) {
      failedValidAddressAttempts += 1;
    }

    Object.assign(state, {
      failedValidAddressAttempts,
      [addressType]: {
        ...state[addressType],
        amsStatusCode,
      },
    });
  },

  SET_MILITARY_ADDRESS(state, { addressType, military }) {
    Object.assign(state, {
      [addressType]: {
        ...state[addressType],
        military,
      },
    });
  },

  SET_PERSON_ERRORS(state, { personType, errors }) {
    Object.assign(state, {
      errors: {
        ...state.errors,
        [personType]: errors,
      },
    });
  },
};

const getters = {
  ...vuexTimeBomb.getters(),
  ...make.getters(defaultState),

  costOfCoa({ config }) {
    const calculatedAmount = (config.icoaAmount || 105) / 100;
    return calculatedAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  },

  showGpOutageBanner({ config }) {
    return config.showGpOutageBanner || false;
  },

  rapidKeyCreds(state) {
    return {
      origin: state.config.rapidKeyUrl,
      token: state.token,
      visitId: state.visitId,
    };
  },
  isGlobalPaymentExempt({ dcoa }) {
    return !!dcoa;
  },

  needsHydration(state) {
    return !!state.uuid;
  },

  humanReadableOldAddress(state) {
    return `${state.oldAddress.street} ${state.oldAddress.city}, ${state.oldAddress.state} ${state.oldAddress.zipcode}`;
  },

  humanReadableNewAddress(state) {
    return `${state.newAddress.street} ${state.newAddress.city}, ${state.newAddress.state} ${state.newAddress.zipcode}`;
  },

  stateOptions() {
    /** */
    const addAbreviation = ({ value, label }) => ({
      value,
      label: `${label} (${value})`,
    });

    return [
      ...dropdownData.states.map(addAbreviation),
      { ...dropdownData.delimiter, value: uuidv4() },
      ...dropdownData.militaryPostOffices.map(addAbreviation),
      { ...dropdownData.delimiter, value: uuidv4() },
      ...dropdownData.territories.map(addAbreviation),
    ];
  },
  showNewPrivateMailBoxField(state) {
    return (
      !!state.newAddress.privateMailbox
      || (state.errors.newAddress
        && [
          'CCR1CmraAddressException',
          'CmraAddressException',
        ].indexOf(state.errors.newAddress) >= 0)
    );
  },
  showNewPOBoxField(state) {
    return (
      !!state.newAddress.poBox
      || (state.errors.newAddress
        && [
          'R777AddressException',
          'PoBoxRequiredException',
          'NewPoBoxRequiredException',
          'InvalidPrimaryPoBoxException',
        ].indexOf(state.errors.newAddress) >= 0)
    );
  },
  newAddressFieldError(state) {
    switch (state.errors.newAddress) {
      case 'AmsInvalidAddressException':
        return 'We cannot find the address you\'re moving to in our records. Please modify your address and then click "Continue."';

      case 'PrimaryNumberMissingException':
        return messaging.validation.api.street.newPrimaryNumberMissing;

      case 'NewAddressFieldFullAddressException':
        return messaging.validation.api.street.fullAddressFieldException;

      case 'C777AddressException':
        return 'Please enter your PO Box address rather than your street style address.';

      case 'UndeliverableAddressException':
        return messaging.validation.api.street.undeliverableAddress;

      case 'AmsInvalidStreetAddressException':
        return messaging.validation.api.street.invalidStreet;

      case 'InvalidPrimaryInformationException':
        return messaging.validation.api.street.newInvalidPrimaryNumber;

      case 'InvalidSecondaryInformationException':
        return messaging.validation.api.street.missingSecondaryNumber;

      case 'OldAndNewAddressMatchException':
        return messaging.validation.api.street.addressesMatch;

      case 'UnneededSecondaryInformationException':
      case 'CCNotZip11Exception':
        return 'According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy before continuing, and then click "Continue."';

      case 'TooManyComponentsException':
        return 'The street address field cannot include the city, state or ZIP';

      default:
        return '';
    }
  },

  newAddressPMBFieldError({ errors }) {
    switch (errors.newAddress) {
      case 'CmraAddressException':
        return 'To forward mail to a commercial mail receiving agency, you must include the private mailbox number. Please enter the Private Mailbox Number in the "Private Mailbox" field, then click "Continue". USPS will standardize your address upon submission.';

      case 'CCR1CmraAddressException':
        return messaging.validation.api.street.CCR1PrivateMailbox;

      default:
        return '';
    }
  },

  newAddressIsCMRA({ errors }) {
    return errors.newAddress === 'CmraAddressException';
  },

  showOldPOBoxField(state) {
    return !!state.oldAddress.poBox
      || (state.errors.oldAddress
        && [
          'R777AddressException',
          'PoBoxRequiredException',
          'OldPoBoxRequiredException',
          'InvalidPrimaryPoBoxException',
        ].indexOf(
          state.errors.oldAddress,
        ) >= 0);
  },

  oldAddressFieldError(state) {
    switch (state.errors.oldAddress) {
      case 'AmsInvalidStreetAddressException':
      case 'AmsMultipleAddressException':
        return messaging.validation.api.street.invalidStreet;

      case 'InvalidPrimaryPoBoxException':
        return messaging.validation.api.street.invalidPrimaryPoBox;

      case 'InvalidPrimaryInformationException':
        return messaging.validation.api.street.oldInvalidPrimaryNumber;

      case 'InvalidSecondaryInformationException':
        return messaging.validation.api.street.missingSecondaryNumber;

      case 'UnneededSecondaryInformationException':
        return messaging.validation.api.street.unneededSecondaryNumber;

      case 'CCR1CmraAddressException':
        return messaging.validation.api.street.CCR1PrivateMailbox;

      case 'OldAndNewAddressMatchException':
        return messaging.validation.api.street.addressesMatch;

      case 'MilitaryAddressException':
        return messaging.validation.api.street.oldAddressMovingFromMilitaryToBusiness;

      default: return '';
    }
  },

  oldAddressIsUniqueZip({ errors }) {
    return errors.oldAddress === 'UniqueZipException';
  },

  oldAddressIsCMRA({ errors }) {
    return errors.oldAddress === 'CmraAddressException';
  },

  oldAddressIneligibleBizMover({ errors }) {
    return errors.oldAddress === 'BusinessMoverTypeException';
  },

  oldZipcodeFieldError(state) {
    if (state.errors.oldAddress === 'Zip9NotEligibleException') {
      return messaging.validation.api.zip.zip9;
    }

    return '';
  },

  isOldAddressValid(state, { oldAddressFieldError, oldZipcodeFieldError }) {
    return !oldAddressFieldError && !oldZipcodeFieldError;
  },

  isNewAddressValid(state, {
    newAddressFieldError,
    newAddressPMBFieldError,
  }) {
    if (newAddressFieldError || newAddressPMBFieldError) return false;

    return true;
  },

  newAddressAmsMatch(state) {
    return isAmsMatch(state.newAddress.amsStatusCode);
  },

  newAddressCanBeOverridden({ // state
    newAddress,
    failedValidAddressAttempts,
    newAddressCanBeOverridden,
  }, { // getters
    newAddressAmsMatch,
  }) {
    let eligibleForOverride = false;
    if (
      // in case we get an invalid 200 response from backend
      (newAddress.amsStatusCode === 'INVALID_STREET_ADDR' && !newAddressAmsMatch)
      // If 422 says address can be overriden
      || newAddressCanBeOverridden
    ) {
      eligibleForOverride = true;
    }

    return eligibleForOverride && failedValidAddressAttempts > 1;
  },

  isMoverValid(state, { moverFirstNameError, moverLastNameError }) {
    if (state.moverType === 'business') return true;
    return !moverFirstNameError && !moverLastNameError;
  },

  isMilitary({ oldAddress }) {
    return oldAddress.military;
  },

  is3rdAddressEligible({ moverType, forwardType }) {
    if (moverType === 'business' || forwardType === 'temporary') return false;
    return true;
  },

  moverFirstNameError(state) {
    return state.errors.mover && state.errors.mover.firstName;
  },

  moverLastNameError(state) {
    return state.errors.mover && state.errors.mover.lastName;
  },

  isbillingAddressValid(ctx, { billingAddressFieldError }) {
    return !billingAddressFieldError;
  },

  billingAddressFieldError({ errors }) {
    switch (errors.billingAddress) {
      case 'WatchlistException':
        return 'We are unable to proceed with this order using the billing address above, please select the new or old address as the billing address to continue.';

      case 'PrimaryNumberMissingException':
        return 'The street number of the street address is missing. Please add the street number and click "Continue" to proceed.';

      case 'NewAddressFieldFullAddressException':
        return messaging.validation.api.street.fullAddressFieldException;

      case 'C777AddressException':
        return 'Please enter your PO Box address rather than your street style address.';

      case 'UndeliverableAddressException':
        return messaging.validation.api.street.undeliverableAddress;

      case 'InvalidStreetAddressException':
      case 'AmsInvalidStreetAddressException':
        return messaging.validation.api.street.invalidStreet;

      case 'InvalidPrimaryInformationException':
        return 'We cannot find the address that you entered in our records. Please check the spelling of all address information to ensure it is accurate.';

      case 'InvalidSecondaryInformationException':
        return 'A unit or apartment number is either missing, incomplete or invalid. Please add the correct number/designation after the street name in the "Street" field, then click "Continue." USPS will standardize your address upon submission.';

      case 'OldAndNewAddressMatchException':
        return messaging.validation.api.street.addressesMatch;

      case 'UnneededSecondaryInformationException':
      case 'CCNotZip11Exception':
        return 'According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy before continuing, and then click "Continue."';

      case 'TooManyComponentsException':
        return 'The street address field cannot include the city, state or ZIP';

      default:
        return '';
    }
  },

  showBillingPOBox(state) {
    return !!state.billingAddress.poBox
      || (state.errors.billingAddress
        && [
          'R777AddressException',
          'PoBoxRequiredException',
          'InvalidPrimaryPoBoxException',
        ].indexOf(
          state.errors.billingAddress,
        ) >= 0);
  },

  showBillingPrivateMailBox(state) {
    return (
      !!state.billingAddress.privateMailbox
      || (state.errors.billingAddress
        && [
          'CCR1CmraAddressException',
          'CmraAddressException',
        ].indexOf(state.errors.billingAddress) >= 0)
    );
  },

  billingAddressPMBFieldError({ errors }) {
    switch (errors.billingAddress) {
      case 'CmraAddressException':
        return 'You must include the private mailbox number. Please enter the Private Mailbox Number in the "Private Mailbox" field, then click "Continue". USPS will standardize your address upon submission.';

      case 'CCR1CmraAddressException':
        return messaging.validation.api.street.CCR1PrivateMailbox;

      default:
        return '';
    }
  },

};

const actions = ({
  MGO_API,
  clientId,
}) => ({
  ...make.actions(defaultState),

  reset({ commit }) {
    commit('RESET_STATE');
  },

  async pageView({ state }, {
    deviceType,
    pageName,
    adgid,
    gclid,
    source,
  }) {
    const routeMap = {
      'mycoa-start': 20000,
      'mycoa-who-is': [20031, 20040, 20041, 20043, 20044].join(','),
      'mycoa-move-type': [20030, 20010, 20042].join(','),
      // 'Move Combined': [20031, 20040, 20041, 20043, 20044, 20030, 20010, 20042],
      'mycoa-summary': 20045,
      'mycoa-identify': 20110,
      // 'Age Verification': 20110,
      payment: 2118,
      'mycoa-savings': 1471,
      'mycoa-confirmation': 2420,
      // 'Confirm Order MCOA': 2420,
      'mycoa-error': 500,
    };

    if ((state.uuid) && routeMap[pageName]) {
      const pageId = routeMap[pageName].toString();
      post(`${MGO_API}/mgo-api/api/v1/e/pv`, {
        uuid: state.uuid,
        deviceType,
        pageName,
        /* eslint-disable camelcase */
        context_campaign_adgid: adgid,
        context_campaign_gclid: gclid,
        context_campaign_source: source,
        /* eslint-enable camelcase */
        [pageId.indexOf(',') >= 0 ? 'viewCode' : 'pageId']: pageId,
      }, {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
    }
  },

  async getFullCOA({ dispatch }) {
    const coa = await dispatch('getCOA');
    const newAddress = await dispatch('getAddress', 'newAddress');
    const oldAddress = await dispatch('getAddress', 'oldAddress');
    const mover = await dispatch('getPerson', 'mover');

    return {
      coa,
      newAddress,
      oldAddress,
      mover,
    };
  },

  optOutReminderEmail({ state }) {
    post(`${MGO_API}/mgo-api/api/v1/e/unsubscribe/${state.uuid}?type=reminder`, {}, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  async getConfig({ state, commit }) {
    const response = await get(`${MGO_API}/mgo-api/api/v1/app/config`, {}, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });

    commit('SET_CONFIG', response);
  },

  async getDCOA({ state, commit }) {
    const { dcoa } = await get(
      `${MGO_API}/mgo-api/api/v1/coa/${state.uuid}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      },
    );

    commit('SET_DCOA', dcoa || false);
  },

  verifyAge({ state }, birthDate) {
    return post(`${MGO_API}/mgo-api/api/v1/identity/id-verification/${state.uuid}?birthdate=${dateToMGODate(birthDate)}`, {
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  verifyBizDCOA({ state }) {
    return post(`${MGO_API}/mgo-api/api/v1/identity/id-verification/${state.uuid}`, {
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  /**
   * gets MGO bearer token to sign all other requests
   * @param {Object} context - Vuex Context
   * @param {Function} context.commit
   */
  async getToken({ commit }) {
    const { access_token: accessToken } = await post(`${MGO_API}/mgo-auth/oauth/token`, {}, {
      params: {
        grant_type: 'client_credentials',
        client_id: clientId,
      },
    });

    if (accessToken) {
      commit('SET_TOKEN', accessToken);
    }
  },

  /**
   * Create a COA if it does not exist
   * @param {Object} context - Vuex Context
   * @param {Function} context.commit
   * @param {Object} context.state
   * @param {String} context.state.uuid - COA UUID
   */
  async createCOA({ state, commit, dispatch }) {
    if (state.uuid) return;
    if (!state.token) await dispatch('getToken');

    const { uuid, visitId } = await post(`${MGO_API}/mgo-api/api/v1/coa`, {
      origin: 'MMICOA',
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
        'content-type': 'application/json',
      },
    });

    if (uuid) {
      commit('SET_UUID', uuid);
    }

    if (visitId) {
      commit('SET_VISIT_ID', visitId);
    }

    const now = new Date();
    const expirationDate = new Date(now);
    expirationDate.setSeconds(now.getSeconds() + (60 * 60)); // bomb goes off in an hour
    commit('SET_TIME_BOMB', expirationDate);
  },

  updateCOA({ state }) {
    return put(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}`,
      getUpdatePayload(state),
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
  },

  getCOA({ state }) {
    return get(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}`, {}, {
      headers: { Authorization: `Bearer ${state.token}` },
    });
  },

  async saveAddress({ state, dispatch, commit }, addressType) {
    const {
      military, amsStatusCode, ...response
    } = state[addressType].uuid
      ? await dispatch('updateAddress', addressType)
      : await dispatch('createAddress', addressType);

    const commitObject = {
      addressType,
      exception: '',
    };

    if (addressType === 'newAddress') {
      const { overrideAllowed } = response.xhr || { overrideAllowed: false };
      commit('SET_NEW_ADDRESS_CAN_BE_OVERRIDDEN', overrideAllowed);
    }

    if (response.xhr && response.xhr.exception) {
      commitObject.exception = response.xhr.exception;
      dispatch('submitAddressException', response.xhr.exception);
    } else if (amsStatusCode === 'INVALID_STREET_ADDR') {
      dispatch('submitAddressException', 'INVALID_STREET_ADDR');
    } else {
      commit('SET_MILITARY_ADDRESS', {
        addressType,
        military: military || false,
      });
    }

    commit('SET_ADDRESS_ERROR', commitObject);
    commit('SET_ADDRESS_AMS_STATUS', {
      addressType,
      amsStatusCode: amsStatusCode || '',
    });

    return response.xhr || {};
  },

  async createAddress({ state, commit }, addressType) {
    if (state[addressType].uuid) return {};

    const { uuid, ...xhr } = await post(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/addresses`, {
      orderUuid: state.uuid,
      streetAddress: state[addressType].street,
      city: state[addressType].city,
      state: state[addressType].state,
      zipCode: state[addressType].zipcode,
      urbanizationName: state[addressType].urbanizationName,
      poBoxNumber: state[addressType].poBox,
      privateMailbox: state[addressType].privateMailbox || '',
      overrideAms: state[addressType].overrideAms || false,
      recentConstruction: state[addressType].recentConstruction || false,
      addressType: addressMap[addressType],
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });

    if (uuid) {
      commit('SET_NESTED_UUID', { property: addressType, uuid });
    }

    return { uuid, ...xhr };
  },

  updateAddress({ state }, addressType) {
    return put(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/addresses/${state[addressType].uuid}`, {
      uuid: state[addressType].uuid,
      orderUuid: state.uuid,
      streetAddress: state[addressType].street,
      city: state[addressType].city,
      state: state[addressType].state,
      zipCode: state[addressType].zipcode,
      urbanizationName: state[addressType].urbanizationName,
      poBoxNumber: state[addressType].poBox,
      privateMailbox: state[addressType].privateMailbox || '',
      overrideAms: state[addressType].overrideAms || false,
      recentConstruction: state[addressType].recentConstruction || false,
      addressType: addressMap[addressType],
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  getAddress({ state }, addressType) {
    return get(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/addresses?type=${addressMap[addressType]}`, {}, {
      headers: { Authorization: `Bearer ${state.token}` },
    });
  },

  async savePerson({ state, commit, dispatch }, personType) {
    const { xhr } = state[personType].uuid
      ? await dispatch('updatePerson', personType)
      : await dispatch('createPerson', personType);

    const commitObject = {};

    if (xhr && xhr.fieldErrors) {
      xhr.fieldErrors.forEach(({ field, message }) => {
        Object.assign(commitObject, {
          ...commitObject,
          [field]: message,
        });
      });
    }

    commit('SET_PERSON_ERRORS', { personType, errors: commitObject });
  },

  async createPerson({ state, commit }, personType) {
    if (state[personType].uuid) return {};

    const payload = {
      orderUuid: state.uuid,
      firstName: state.moverType !== 'business' ? state[personType].firstName : null,
      lastName: state.moverType !== 'business' ? state[personType].lastName : null,
      email: state[personType].email,
      emailConfirm: state[personType].emailConfirm,
      personType: personMap[personType],
      [`${state[personType].phoneType.toLowerCase()}Phone`]: state[personType].phone,
      suffix: state[personType].suffix,
    };
    const { uuid, ...xhr } = await post(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/persons`, pruneObject(payload), {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });

    if (uuid) {
      commit('SET_NESTED_UUID', { property: personType, uuid });
    }

    return { uuid, ...xhr };
  },

  updatePerson({ state }, personType) {
    const payload = {
      orderUuid: state.uuid,
      uuid: state[personType].uuid,
      firstName: state.moverType !== 'business' ? state[personType].firstName : null,
      lastName: state.moverType !== 'business' ? state[personType].lastName : null,
      email: state[personType].email,
      emailConfirm: state[personType].emailConfirm,
      personType: personMap[personType],
      [`${state[personType].phoneType.toLowerCase()}Phone`]: state[personType].phone,
      suffix: state[personType].suffix,
    };
    return put(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/persons/${state[personType].uuid}`, pruneObject(payload), {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  getPerson({ state }, personType) {
    return get(`${MGO_API}/mgo-api/api/v1/coa/${state.uuid}/persons?type=${personMap[personType]}`, {}, {
      headers: { Authorization: `Bearer ${state.token}` },
    });
  },

  getGlobalPaymentUrl({ state }, { returnUrl, postbackUrl }) {
    return post(`${MGO_API}/mgo-api/api/v1/payment`, {
      orderUuid: state.uuid,
      returnUrl,
      postbackUrl,
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  async commit({ state, commit }, {
    orderNumber,
    cuid,
  }) {
    const url = `${MGO_API}/mgo-api/api/v2/coa/${state.uuid}/commit`;
    const item = url.split('?');
    const outUrl = url?.replace(`?${item[1]}`, `/commit?${item[1]?.replace('/commit', '')}`);

    const { cfpsId } = await post(outUrl, {
      orderNumber: orderNumber || null,
      cuid: cuid || null,
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });

    commit('SET_CFPSID', cfpsId || '');
    // check for success on DCOA
    if (cfpsId === null) {
      return true;
    }

    return !!cfpsId;
  },

  isFamilySettingValid(ctx, { sameLastName, sameLastNameStay }) {
    return isFamilySettingsValid(sameLastName, sameLastNameStay);
  },

  getFamilyValidationError(ctx, { sameLastName, sameLastNameStay }) {
    return getFamilyValidationError(sameLastName, sameLastNameStay);
  },

  triggerRealTimeEmails({ state }) {
    return post(`${MGO_API}/mgo-api/api/v1/email/${state.uuid}/send`, {}, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  submitAddressException({ state }, exception) {
    return post(`${MGO_API}/mgo-api/api/v1/exception/${state.uuid}`, {
      exception,
      oldAddress: {
        streetAddress: state.oldAddress.street || null,
        city: state.oldAddress.city || null,
        state: state.oldAddress.state || null,
        zipCode: state.oldAddress.zipcode || null,
      },
      newAddress: {
        streetAddress: state.newAddress.street || null,
        city: state.newAddress.city || null,
        state: state.newAddress.state || null,
        zipCode: state.newAddress.zipcode || null,
      },
      thirdAddress: {
        streetAddress: state.billingAddress.street || null,
        city: state.billingAddress.city || null,
        state: state.billingAddress.state || null,
        zipCode: state.billingAddress.zipcode || null,
      },
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },
});

export default ({
  MGO_API,
  clientId,
}) => ({
  namespaced: true,
  state: defaultState,
  mutations,
  getters,
  actions: actions({
    MGO_API,
    clientId,
  }),
});
